// Go modules in the console (ADR-0038): prefix rules as you type, version order, retracted versions from go.mod, and
// the GOPROXY, GONOSUMDB and GOAUTH snippets for the Connect page.
import type { Domain } from "../api/types";

/** The same rules as `prefixProblem` in apps/registry/src/go/rules.ts. The registry checks them again. */
const MAX_PREFIX_LENGTH = 200;
const SHARED_HOSTS = ["github.com", "gitlab.com", "bitbucket.org", "codeberg.org", "dev.azure.com"];
const RESERVED_HOSTS = [
  "golang.org",
  "google.golang.org",
  "go.googlesource.com",
  "gopkg.in",
  "go.dev",
  "pkg.go.dev",
  "k8skill.com",
  "katskill.com",
  "prego.k8skill.com",
];
const PATH_ELEMENT_RE = /^[A-Za-z0-9_~-](?:[A-Za-z0-9._~-]*[A-Za-z0-9_~-])?$/;
const FIRST_ELEMENT_RE = /^[a-z0-9-][a-z0-9.-]*$/;

/** Why a module path prefix is refused, or null when it is valid (ADR-0038 section 2). */
export function goPrefixProblem(prefix: string): string | null {
  if (prefix.length === 0 || prefix.length > MAX_PREFIX_LENGTH) return "prefix must be 1 to 200 characters";
  if (prefix !== prefix.toLowerCase()) return "prefix must be lowercase";
  const parts = prefix.split("/");
  const host = parts[0]!;
  if (
    !FIRST_ELEMENT_RE.test(host) ||
    !host.includes(".") ||
    host.startsWith("-") ||
    !parts.every((p) => PATH_ELEMENT_RE.test(p))
  ) {
    return "prefix must be a Go module path whose first element has a dot";
  }
  for (const r of RESERVED_HOSTS) {
    if (host === r || host.endsWith(`.${r}`)) return `prefix under ${r} is reserved`;
  }
  if (SHARED_HOSTS.includes(host) && parts.length < 2) {
    return `prefix on ${host} needs at least two elements, for example ${host}/<org>`;
  }
  return null;
}

/** True when the host of the prefix is a verified domain of the tenant, or under one. Only a hint for consumers. */
export function onVerifiedDomain(prefix: string, domains: Pick<Domain, "domain" | "state">[]): boolean {
  const host = prefix.split("/")[0]!;
  return domains.some((d) => d.state === "verified" && (host === d.domain || host.endsWith(`.${d.domain}`)));
}

/** True when one prefix is equal to the other or under it. Then the first GOPROXY entry wins (ADR-0038 13). */
export function prefixesOverlap(a: string, b: string): boolean {
  return a === b || a.startsWith(`${b}/`) || b.startsWith(`${a}/`);
}

interface ParsedVersion {
  major: number;
  minor: number;
  patch: number;
  pre: string[];
}

const SEMVER_RE = /^v(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-([0-9A-Za-z-]+(?:\.[0-9A-Za-z-]+)*))?$/;

function parse(v: string): ParsedVersion | null {
  const m = SEMVER_RE.exec(v);
  if (!m) return null;
  return { major: Number(m[1]), minor: Number(m[2]), patch: Number(m[3]), pre: m[4] ? m[4].split(".") : [] };
}

/** SemVer precedence of two Go versions. A version that does not parse sorts first. */
export function compareGoVersions(x: string, y: string): number {
  const a = parse(x);
  const b = parse(y);
  if (!a || !b) return a ? 1 : b ? -1 : x < y ? -1 : x > y ? 1 : 0;
  for (const k of ["major", "minor", "patch"] as const) if (a[k] !== b[k]) return a[k] - b[k];
  if (a.pre.length === 0 || b.pre.length === 0) return b.pre.length - a.pre.length;
  for (let i = 0; i < Math.min(a.pre.length, b.pre.length); i++) {
    const p = a.pre[i]!;
    const q = b.pre[i]!;
    if (p === q) continue;
    const pn = /^\d+$/.test(p);
    const qn = /^\d+$/.test(q);
    if (pn && qn) return Number(p) - Number(q);
    if (pn !== qn) return pn ? -1 : 1;
    return p < q ? -1 : 1;
  }
  return a.pre.length - b.pre.length;
}

/** The newest version: the highest release, else the highest prerelease (as Go's `@latest`). */
export function latestGoVersion<T extends { version: string }>(versions: T[]): T | undefined {
  const sorted = [...versions].sort((a, b) => compareGoVersions(b.version, a.version));
  return sorted.find((v) => parse(v.version)?.pre.length === 0) ?? sorted[0];
}

/** A version or an inclusive range from a `retract` directive. */
export type Retraction = { low: string; high: string };

function stripComment(line: string): string {
  const i = line.indexOf("//");
  return i < 0 ? line : line.slice(0, i);
}

function retractEntry(text: string): Retraction | null {
  const t = text.trim();
  if (!t) return null;
  const range = /^\[\s*(v\S+?)\s*,\s*(v\S+?)\s*\]$/.exec(t);
  if (range) return { low: range[1]!, high: range[2]! };
  const one = /^(v\S+)$/.exec(t);
  return one ? { low: one[1]!, high: one[1]! } : null;
}

/** The `retract` directives of a go.mod: single lines and blocks (`retract ( ... )`). */
export function goModRetractions(goMod: string): Retraction[] {
  const out: Retraction[] = [];
  const lines = goMod.split(/\r?\n/).map(stripComment);
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!.trim();
    if (!/^retract(\s|\(|$)/.test(line)) continue;
    const rest = line.slice("retract".length).trim();
    if (rest.startsWith("(")) {
      let inner = rest.slice(1);
      while (!inner.includes(")") && i + 1 < lines.length) {
        const r = retractEntry(inner);
        if (r) out.push(r);
        inner = lines[++i]!;
      }
      const r = retractEntry(inner.slice(0, inner.indexOf(")")));
      if (r) out.push(r);
    } else {
      const r = retractEntry(rest);
      if (r) out.push(r);
    }
  }
  return out;
}

/** True when the version is in one of the retractions. */
export function isRetracted(version: string, retractions: Retraction[]): boolean {
  return retractions.some(
    (r) => compareGoVersions(version, r.low) >= 0 && compareGoVersions(version, r.high) <= 0,
  );
}

/** The GOPROXY base of a namespace. The namespace is always written, "default" too (ADR-0038 section 2). */
export function goProxyUrl(goHost: string, tenant: string, ns: string): string {
  const scheme = /^(localhost|127\.0\.0\.1)(:|$)/.test(goHost) ? "http" : "https";
  return `${scheme}://${goHost}/t/${tenant}/${ns}`;
}

/** GONOSUMDB with exactly the tenant's prefixes, so all other modules are still checked against sum.golang.org. */
export function goNoSumDb(prefixes: string[]): string {
  return [...prefixes].sort().join(",");
}

export interface GoSnippetInput {
  goHost: string;
  tenant: string;
  namespace: string;
  /** Live module path prefixes of the tenant. */
  prefixes: string[];
}

const PREFIX_PLACEHOLDER = "<add a Go module path in Settings>";

/** The three `go env -w` lines that `prego setup go` runs (ADR-0038 section 4.1). */
export function goEnvLines(i: GoSnippetInput): string {
  const nosum = i.prefixes.length ? goNoSumDb(i.prefixes) : PREFIX_PLACEHOLDER;
  return [
    `go env -w GOPROXY='${goProxyUrl(i.goHost, i.tenant, i.namespace)},https://proxy.golang.org,direct'`,
    `go env -w GONOSUMDB='${nosum}'`,
    "go env -w GOAUTH='prego go-auth'",
    "",
  ].join("\n");
}

/** The laptop setup with the Prego tool (ADR-0038 section 4.1). */
export function goSetupCommands(i: GoSnippetInput): string {
  return [
    "# Go 1.24 or newer. The prego tool comes from prego-clients.",
    "prego setup go",
    "# prego setup go runs these three lines. You can also set them by hand:",
    goEnvLines(i),
  ].join("\n");
}

/** The `.netrc` fallback for old Go and build tools without GOAUTH (ADR-0038 section 4.3). */
export function goNetrc(i: GoSnippetInput): string {
  return [`machine ${i.goHost}`, `  login ${i.tenant}`, "  password <read-only API key>", ""].join("\n");
}

/** GitLab CI: build with `prego setup go`, publish on tag pipelines with `prego go publish` (ADR-0038 section 12). */
export function gitlabCiGoYaml(i: GoSnippetInput & { audience: string }): string {
  return [
    "build:",
    "  image: golang:1.24",
    "  id_tokens:",
    "    PREGO_ID_TOKEN:",
    `      aud: ${i.audience}`,
    "  script:",
    "    - prego setup go",
    "    - go build ./...",
    "",
    "publish:",
    "  image: golang:1.24",
    "  rules:",
    "    - if: $CI_COMMIT_TAG",
    "  id_tokens:",
    "    PREGO_ID_TOKEN:",
    `      aud: ${i.audience}`,
    "  script:",
    `    - prego go publish --namespace ${i.namespace} --tag "$CI_COMMIT_TAG"`,
    "",
  ].join("\n");
}

/** GitHub Actions: the same two jobs (ADR-0038 section 12). */
export function githubActionsGoYaml(i: GoSnippetInput): string {
  return [
    "permissions:",
    "  id-token: write",
    "  contents: read",
    "jobs:",
    "  build:",
    "    runs-on: ubuntu-latest",
    "    steps:",
    "      - uses: actions/checkout@v4",
    "      - uses: actions/setup-go@v5",
    "        with:",
    "          go-version: '1.24'",
    "      - run: prego setup go",
    "      - run: go build ./...",
    "      - if: startsWith(github.ref, 'refs/tags/v')",
    `        run: prego go publish --namespace ${i.namespace} --tag "$GITHUB_REF_NAME"`,
    "",
  ].join("\n");
}

/** `go get` for a module version. */
export function goGetCommand(path: string, version?: string): string {
  return `go get ${path}@${version ?? "latest"}`;
}
